import type { OHovRunRecords, OHovSnapshot, OHovState } from "../types";
import {
  formatForContext,
  formatPercent,
} from "@/features/hoverIdle/resources/numberFormat";
import {
  compareHuge,
  huge,
  hugeOne,
  hugeZero,
  maxHuge,
  type HugeNumber,
} from "@/shared/numbers";
import { formatOHovRunTime } from "./oHovTimeFormat";

// OHov1 is the huge wall the player must pass to leave the Hov biome.
// 1.8e308 is its design-level target value, kept as a HugeNumber-safe string so
// the gate never depends on the JavaScript number limit itself.
export const OHOV_UNLOCK_HOV_THRESHOLD = "1.8e308";
const OHOV_UNLOCK_HOV_THRESHOLD_LOG10 = huge(OHOV_UNLOCK_HOV_THRESHOLD)
  .log10()
  .toNumber();

// Concept: tier `n` starts at boundaryHov(n) = 1.8 * (n+1) * 10^(308*(n+1)).
// The first tier (n=0) starts at the OHov unlock threshold (1.8e308).
const OHOV_HOV_CORE_TIER_BOUNDARY_MANTISSA_TENTHS = 18;
const OHOV_HOV_CORE_TIER_BOUNDARY_MANTISSA_BASE =
  OHOV_HOV_CORE_TIER_BOUNDARY_MANTISSA_TENTHS / 10;
const OHOV_HOV_CORE_TIER_BOUNDARY_EXPONENT_STRIDE = 308;
const OHOV_HOV_CORE_EARLY_GAIN_MULTIPLIER = 2;
const OHOV_HOV_CORE_LATE_GAIN_MULTIPLIER = 10;
// boundaryHov(9) is 1.8e3081. Segments ending there interpolate toward x2;
// later segments interpolate toward x10 from that connected gain value.
const OHOV_HOV_CORE_LATE_PACE_START_TIER = 9;
// Safety cap so we never iterate forever for absurd inputs. Realistic player
// tiers stay well below this; the cap exists for inf-like or malformed Hov.
const OHOV_HOV_CORE_TIER_MAX = 1024;

// "Where did this OHov execution come from?" — kept here so the OHov model
// surface owns the vocabulary; PlayLog imports the same union via its own
// type alias so the runtime and analysis layers stay in lockstep.
// "wall-start" tags the OHov-equivalent run boundary applied at the start of
// a wall challenge. It is not a wall clear and may grant rewards only when
// Hov was already at or past the OHov threshold.
export type OHovExecutionSource =
  | "manual"
  | "mp"
  | "auto"
  | "wall-start"
  | "unknown";

export function createDefaultOHovRunRecords(nowMs: number): OHovRunRecords {
  return {
    currentRunStartedAtMs: normalizeStartedAtMs(nowMs),
    lastRunDurationMs: null,
    fastestRunDurationMs: null,
    currentRunGameElapsedMs: 0,
    fastestRunGameDurationMs: null,
  };
}

export function createDefaultOHovState(nowMs: number = 0): OHovState {
  return {
    count: 0,
    isUnlocked: false,
    power: hugeZero(),
    hovCore: hugeZero(),
    records: createDefaultOHovRunRecords(nowMs),
  };
}

export function isOHovUnlocked(hov: HugeNumber): boolean {
  return hov.isFinite() && compareHuge(hov, OHOV_UNLOCK_HOV_THRESHOLD) >= 0;
}

export function canExecuteOHov(options: {
  readonly hov: HugeNumber;
  // Also accepts a pre-parsed HugeNumber so hot automation gates can avoid
  // re-parsing a target string on every readiness probe.
  readonly targetHov?: string | HugeNumber;
}): boolean {
  const targetHov = options.targetHov ?? OHOV_UNLOCK_HOV_THRESHOLD;
  return options.hov.isFinite() && compareHuge(options.hov, targetHov) >= 0;
}

export type BuiltInOHovAutoExecutionTargetSource =
  | "base"
  | "outer-wall-influence"
  | "active-wall";

export interface BuiltInOHovAutoExecutionTarget {
  readonly targetHov: string;
  readonly source: BuiltInOHovAutoExecutionTargetSource;
}

export function resolveBuiltInOHovAutoExecutionTarget(options: {
  readonly activeWallTargetHov?: string | null;
  readonly outerWallInfluenceTargetHov?: string | null;
}): BuiltInOHovAutoExecutionTarget {
  if (
    options.activeWallTargetHov !== null &&
    options.activeWallTargetHov !== undefined
  ) {
    return {
      targetHov: options.activeWallTargetHov,
      source: "active-wall",
    };
  }
  if (
    options.outerWallInfluenceTargetHov !== null &&
    options.outerWallInfluenceTargetHov !== undefined
  ) {
    return {
      targetHov: options.outerWallInfluenceTargetHov,
      source: "outer-wall-influence",
    };
  }
  return {
    targetHov: OHOV_UNLOCK_HOV_THRESHOLD,
    source: "base",
  };
}

/**
 * Decide whether built-in OHov auto should fire at its resolved target.
 *
 * Built-in OHov auto fires whenever Hov reaches the resolved built-in auto
 * target: the base `1.8e308 Hov` threshold, the Outer Wall influence target
 * after Wall V clear, or an active wall clear target. The firing target is
 * fully expressed by `resolveBuiltInOHovAutoExecutionTarget`, so there is no
 * separate suppression gate. The OHov MP assignment-form unlock is intentionally
 * unrelated to this path.
 */
export function shouldAutoExecuteOHov(options: {
  readonly hov: HugeNumber;
  readonly target: BuiltInOHovAutoExecutionTarget;
}): boolean {
  return canExecuteOHov({
    hov: options.hov,
    targetHov: options.target.targetHov,
  });
}

export function isOHovAreaUnlocked(options: {
  readonly oHovState: Pick<OHovState, "count" | "isUnlocked">;
  readonly hov: HugeNumber;
}): boolean {
  return (
    options.oHovState.isUnlocked ||
    options.oHovState.count > 0 ||
    isOHovUnlocked(options.hov)
  );
}

export function calculateOHovProgressRatio(hov: HugeNumber): number {
  if (!hov.isFinite()) return 0;
  if (compareHuge(hov, 0) <= 0) return 0;
  if (compareHuge(hov, OHOV_UNLOCK_HOV_THRESHOLD) >= 0) return 1;

  const targetLog = OHOV_UNLOCK_HOV_THRESHOLD_LOG10;
  const hovLog = hov.log10().toNumber();
  if (!Number.isFinite(targetLog) || targetLog <= 0) return 0;
  if (!Number.isFinite(hovLog) || hovLog <= 0) return 0;

  return clamp01(hovLog / targetLog);
}

// Wall challenges can move the OHov gauge's 100% mark to their clear target
// instead of the normal OHov threshold. Mirrors `calculateOHovProgressRatio`
// but against an arbitrary target so the bar fill stays log-linear toward the
// wall clear point (currently the outer wall at 3.6e616) instead of pinning to
// 100% once the normal threshold is passed.
export function calculateOHovProgressRatioToTarget(
  hov: HugeNumber,
  targetHov: string,
): number {
  if (!hov.isFinite()) return 0;
  if (compareHuge(hov, 0) <= 0) return 0;
  if (compareHuge(hov, targetHov) >= 0) return 1;

  const targetLog = huge(targetHov).log10().toNumber();
  const hovLog = hov.log10().toNumber();
  if (!Number.isFinite(targetLog) || targetLog <= 0) return 0;
  if (!Number.isFinite(hovLog) || hovLog <= 0) return 0;

  return clamp01(hovLog / targetLog);
}

export interface OHovHovCoreTier {
  // Tier index: 0 at the OHov unlock threshold, +1 per HovCore gain boundary
  // crossed. `tier < 0` means the player has not reached the unlock threshold
  // yet, so no HovCore would be granted by Outside reset.
  readonly tier: number;
  // log10 of the current tier's lower boundary (`tier < 0` => log10(threshold)).
  readonly currentBoundaryHovLog10: number;
  // log10 of the next tier's lower boundary. Used to derive within-tier
  // progress; equals `currentBoundaryHovLog10` for sub-threshold inputs.
  readonly nextBoundaryHovLog10: number;
  // Continuous base HovCore gain before execution-time multipliers and final
  // integer flooring.
  readonly baseHovCoreGain: HugeNumber;
  // 0..1 progress from `currentBoundaryHovLog10` to `nextBoundaryHovLog10`.
  // Resets to 0 at every tier boundary so the OHov gauge can recharge.
  readonly progressWithinTier: number;
}

export function resolveOHovHovCoreTier(hov: HugeNumber): OHovHovCoreTier {
  const thresholdLog = OHOV_UNLOCK_HOV_THRESHOLD_LOG10;
  if (!hov.isFinite() || compareHuge(hov, OHOV_UNLOCK_HOV_THRESHOLD) < 0) {
    return {
      tier: -1,
      currentBoundaryHovLog10: thresholdLog,
      nextBoundaryHovLog10: thresholdLog,
      baseHovCoreGain: hugeZero(),
      progressWithinTier: calculateOHovProgressRatio(hov),
    };
  }

  const hovLog = hov.log10().toNumber();
  if (!Number.isFinite(hovLog)) {
    return {
      tier: 0,
      currentBoundaryHovLog10: thresholdLog,
      nextBoundaryHovLog10: oHovTierBoundaryLog10(1),
      baseHovCoreGain: hugeOne(),
      progressWithinTier: 0,
    };
  }

  let tier = 0;
  let currentBoundaryLog = oHovTierBoundaryLog10(0);
  let nextBoundaryLog = oHovTierBoundaryLog10(1);
  while (tier + 1 < OHOV_HOV_CORE_TIER_MAX && hovLog >= nextBoundaryLog) {
    tier += 1;
    currentBoundaryLog = nextBoundaryLog;
    nextBoundaryLog = oHovTierBoundaryLog10(tier + 1);
  }

  const span = nextBoundaryLog - currentBoundaryLog;
  const progressWithinTier =
    span > 0 ? clamp01((hovLog - currentBoundaryLog) / span) : 0;

  return {
    tier,
    currentBoundaryHovLog10: currentBoundaryLog,
    nextBoundaryHovLog10: nextBoundaryLog,
    baseHovCoreGain: oHovTierHovCoreBaseGain(tier, progressWithinTier),
    progressWithinTier,
  };
}

export function resolveOHovHovCoreBaseGainForHov(hov: HugeNumber): HugeNumber {
  return resolveOHovHovCoreTier(hov).baseHovCoreGain;
}

export interface OHovHovCoreAwardedGainInput {
  readonly baseGain: HugeNumber;
  readonly multiplier?: HugeNumber;
}

export function resolveOHovHovCoreAwardedGain(
  input: OHovHovCoreAwardedGainInput,
): HugeNumber {
  const baseGain =
    input.baseGain.isFinite() && compareHuge(input.baseGain, 0) >= 0
      ? input.baseGain
      : hugeZero();
  const multiplier = normalizeOHovHovCoreGainMultiplier(input.multiplier);
  const awardedGain = baseGain.mul(multiplier).floor();
  return awardedGain.isFinite() && compareHuge(awardedGain, 0) >= 0
    ? awardedGain
    : hugeZero();
}

export interface OHovMpAutoExecutionCandidateTargetInput {
  readonly resetTargetHov?: string;
  readonly minimumHovCore: HugeNumber;
  readonly hovCoreGainMultiplier?: HugeNumber;
}

export interface OHovMpAutoExecutionGateInput extends OHovMpAutoExecutionCandidateTargetInput {
  readonly hov: HugeNumber;
  readonly hovCoreGainPreview: HugeNumber;
  readonly minimumElapsedSatisfied: boolean;
}

export interface OHovMpAutoExecutionGateResult {
  readonly canExecute: boolean;
  readonly candidateTargetHov: HugeNumber;
}

interface OHovHovCoreGainScale {
  readonly currentGain: HugeNumber;
  readonly nextGain: HugeNumber;
  readonly currentGainLog10: number;
  readonly nextGainLog10: number;
}

interface OHovHovCoreGainTargetPosition {
  readonly tier: number;
  readonly progressWithinTier: number;
}

export function resolveOHovHovCoreBaseGainTargetHov(
  minimumHovCore: HugeNumber,
): HugeNumber {
  if (
    !minimumHovCore.isFinite() ||
    compareHuge(minimumHovCore, hugeOne()) <= 0
  ) {
    return huge(OHOV_UNLOCK_HOV_THRESHOLD);
  }

  const gainLog10 = minimumHovCore.log10().toNumber();
  if (!Number.isFinite(gainLog10)) {
    return huge(formatOHovHovCoreTierBoundaryHov(OHOV_HOV_CORE_TIER_MAX - 1));
  }

  const position = resolveOHovHovCoreGainTargetPosition(gainLog10);
  if (position.progressWithinTier <= 1e-9) {
    return huge(formatOHovHovCoreTierBoundaryHov(position.tier));
  }
  if (position.progressWithinTier >= 1 - 1e-9) {
    return huge(formatOHovHovCoreTierBoundaryHov(position.tier + 1));
  }

  const currentBoundaryLog = oHovTierBoundaryLog10(position.tier);
  const nextBoundaryLog = oHovTierBoundaryLog10(position.tier + 1);
  const targetLog =
    currentBoundaryLog +
    (nextBoundaryLog - currentBoundaryLog) *
      clamp01(position.progressWithinTier);
  return huge(10).pow(targetLog);
}

export function resolveOHovMpAutoExecutionCandidateTargetHov(
  input: OHovMpAutoExecutionCandidateTargetInput,
): HugeNumber {
  const resetTargetHov = huge(
    input.resetTargetHov ?? OHOV_UNLOCK_HOV_THRESHOLD,
  );
  const hovCoreGainMultiplier = normalizeOHovHovCoreGainMultiplier(
    input.hovCoreGainMultiplier,
  );
  const requiredAwardedHovCoreGain = input.minimumHovCore.ceil();
  const requiredBaseHovCoreGain = maxHuge(
    requiredAwardedHovCoreGain.div(hovCoreGainMultiplier),
    hugeOne(),
  );
  return maxHuge(
    resetTargetHov,
    resolveOHovHovCoreBaseGainTargetHov(requiredBaseHovCoreGain),
  );
}

export function resolveOHovMpAutoExecutionGate(
  input: OHovMpAutoExecutionGateInput,
): OHovMpAutoExecutionGateResult {
  const candidateTargetHov =
    resolveOHovMpAutoExecutionCandidateTargetHov(input);
  const canExecute =
    canExecuteOHov({ hov: input.hov, targetHov: input.resetTargetHov }) &&
    compareHuge(input.hovCoreGainPreview, input.minimumHovCore) >= 0 &&
    input.minimumElapsedSatisfied;

  return {
    canExecute,
    candidateTargetHov,
  };
}

function oHovTierBoundaryLog10(tier: number): number {
  // boundary = 1.8 * (tier + 1) * 10^(308 * (tier + 1))
  // log10(boundary) = log10(1.8) + log10(tier + 1) + 308 * (tier + 1)
  const step = tier + 1;
  return (
    Math.log10(OHOV_HOV_CORE_TIER_BOUNDARY_MANTISSA_BASE) +
    Math.log10(step) +
    OHOV_HOV_CORE_TIER_BOUNDARY_EXPONENT_STRIDE * step
  );
}

function formatOHovHovCoreTierBoundaryHov(tier: number): string {
  const normalizedTier = Math.max(0, Math.floor(tier));
  const step = normalizedTier + 1;
  const mantissaUnits = OHOV_HOV_CORE_TIER_BOUNDARY_MANTISSA_TENTHS * step;
  const mantissaUnitDigits = Math.floor(Math.log10(mantissaUnits));
  const mantissa = mantissaUnits / 10 ** mantissaUnitDigits;
  const exponent =
    OHOV_HOV_CORE_TIER_BOUNDARY_EXPONENT_STRIDE * step - 1 + mantissaUnitDigits;
  const mantissaText = mantissa
    .toFixed(mantissaUnitDigits)
    .replace(/\.?0+$/, "");
  return `${mantissaText}e${exponent}`;
}

function oHovTierHovCoreBaseGain(
  tier: number,
  progressWithinTier: number = 0,
): HugeNumber {
  if (tier < 0) return hugeZero();
  const scale = resolveOHovHovCoreGainScale(tier);
  const progress = clamp01(progressWithinTier);
  if (progress <= 0) return scale.currentGain;
  if (progress >= 1) return scale.nextGain;
  return scale.currentGain.mul(
    scale.nextGain.div(scale.currentGain).pow(progress),
  );
}

function normalizeOHovHovCoreGainMultiplier(
  multiplier: HugeNumber | undefined,
): HugeNumber {
  return multiplier !== undefined &&
    multiplier.isFinite() &&
    compareHuge(multiplier, 0) > 0
    ? multiplier
    : hugeOne();
}

function resolveOHovHovCoreGainScale(tier: number): OHovHovCoreGainScale {
  const normalizedTier = Math.max(0, Math.floor(tier));
  const currentGain = oHovHovCoreGainAtBoundary(normalizedTier);
  const nextGain = oHovHovCoreGainAtBoundary(normalizedTier + 1);
  return {
    currentGain,
    nextGain,
    currentGainLog10: currentGain.log10().toNumber(),
    nextGainLog10: nextGain.log10().toNumber(),
  };
}

function oHovHovCoreGainAtBoundary(tier: number): HugeNumber {
  const normalizedTier = Math.max(0, Math.floor(tier));
  const earlyPacedTierCount = Math.min(
    normalizedTier,
    OHOV_HOV_CORE_LATE_PACE_START_TIER,
  );
  const latePacedTierCount = Math.max(
    0,
    normalizedTier - OHOV_HOV_CORE_LATE_PACE_START_TIER,
  );
  const earlyGain = OHOV_HOV_CORE_EARLY_GAIN_MULTIPLIER ** earlyPacedTierCount;
  if (latePacedTierCount <= 0) return huge(earlyGain);
  if (OHOV_HOV_CORE_LATE_GAIN_MULTIPLIER === 10) {
    return huge(`${earlyGain}e${latePacedTierCount}`);
  }
  return huge(earlyGain).mul(
    huge(OHOV_HOV_CORE_LATE_GAIN_MULTIPLIER).pow(latePacedTierCount),
  );
}

function resolveOHovHovCoreGainTargetPosition(
  gainLog10: number,
): OHovHovCoreGainTargetPosition {
  const targetGainLog10 = Math.max(0, gainLog10);
  const maxTier = OHOV_HOV_CORE_TIER_MAX - 1;
  for (let tier = 0; tier < maxTier; tier += 1) {
    const scale = resolveOHovHovCoreGainScale(tier);
    if (targetGainLog10 <= scale.currentGainLog10 + 1e-9) {
      return { tier, progressWithinTier: 0 };
    }
    if (targetGainLog10 <= scale.nextGainLog10 + 1e-9) {
      const span = scale.nextGainLog10 - scale.currentGainLog10;
      return {
        tier,
        progressWithinTier:
          span > 0
            ? clamp01((targetGainLog10 - scale.currentGainLog10) / span)
            : 0,
      };
    }
  }
  return { tier: maxTier, progressWithinTier: 0 };
}

export function createOHovSnapshot(options: {
  readonly oHovState: OHovState;
  readonly hov: HugeNumber;
  readonly nowMs?: number;
  readonly hovCoreGainPreview?: HugeNumber;
  readonly hovCoreGainSegmentsUnlocked?: boolean;
  readonly manualExecutionUnlocked?: boolean;
  readonly executeTargetHov?: string;
}): OHovSnapshot {
  const tierInfo = resolveOHovHovCoreTier(options.hov);
  const executeTargetHov = options.executeTargetHov;
  // Display target for the progress bar fill, kept distinct from
  // `executeTargetHov` (OHov execute / wall clear gating) and the HovCore gain
  // preview. Walls with a higher clear target also raise the gauge's 100% mark.
  const displayProgressTargetHov =
    executeTargetHov !== undefined &&
    executeTargetHov !== OHOV_UNLOCK_HOV_THRESHOLD
      ? executeTargetHov
      : null;
  // Raised Wall targets take precedence over ordinary HovCore segment progress.
  const progressRatio =
    displayProgressTargetHov !== null
      ? calculateOHovProgressRatioToTarget(
          options.hov,
          displayProgressTargetHov,
        )
      : options.hovCoreGainSegmentsUnlocked === true
        ? tierInfo.progressWithinTier
        : calculateOHovProgressRatio(options.hov);
  const canExecute = canExecuteOHov({
    hov: options.hov,
    targetHov: executeTargetHov,
  });
  const canManualExecute =
    options.manualExecutionUnlocked === true && canExecute;
  const isUnlocked = isOHovAreaUnlocked({
    oHovState: options.oHovState,
    hov: options.hov,
  });
  const records = options.oHovState.records;
  const nowMs = options.nowMs;
  const currentRunElapsedMs =
    typeof nowMs === "number" && Number.isFinite(nowMs)
      ? Math.max(0, nowMs - records.currentRunStartedAtMs)
      : 0;
  const hovCoreGainPreview =
    options.hovCoreGainPreview ??
    resolveOHovHovCoreAwardedGain({
      baseGain: tierInfo.baseHovCoreGain,
    });
  const progressPercentLabel = formatPercent(
    progressRatio * 100,
    progressRatio >= 1 ? 0 : 1,
  );
  const hovCoreGainPreviewLabel = formatForContext(
    hovCoreGainPreview,
    "compact",
  );
  return {
    count: options.oHovState.count,
    power: options.oHovState.power,
    hovCore: options.oHovState.hovCore,
    currentRunElapsedMs,
    lastRunDurationMs: records.lastRunDurationMs,
    fastestRunDurationMs: records.fastestRunDurationMs,
    currentRunGameElapsedMs: records.currentRunGameElapsedMs,
    fastestRunGameDurationMs: records.fastestRunGameDurationMs,
    powerLabel: formatForContext(options.oHovState.power, "compact"),
    hovCoreLabel: formatForContext(options.oHovState.hovCore, "compact"),
    currentRunTimeLabel: formatOHovRunTime(currentRunElapsedMs),
    lastRunTimeLabel: formatOHovRunTime(records.lastRunDurationMs),
    fastestRunTimeLabel: formatOHovRunTime(records.fastestRunDurationMs),
    progressRatio,
    progressPercentLabel,
    gaugeText: canExecute
      ? `+${hovCoreGainPreviewLabel} HovCore`
      : progressPercentLabel,
    isUnlocked,
    canManualExecute,
    canExecute,
    tier: tierInfo.tier,
    progressRatioWithinTier: tierInfo.progressWithinTier,
    hovCoreGainPreview,
    hovCoreGainPreviewLabel,
    executeTargetHov,
  };
}

/**
 * Update OHov run records after a successful run. Returns a new records object
 * that should replace `state.records`. `nowMs` becomes the new start of the
 * next run, and the elapsed duration becomes the last run time. The fastest
 * run is preserved unless the new run beat it.
 *
 * `currentRunGameElapsedMs` is the game-time accumulator the model maintained
 * for the in-flight run; it becomes the game-duration alongside the wall-clock
 * duration. The next run's accumulator is reset to 0.
 */
export function advanceOHovRunRecords(
  current: OHovRunRecords,
  nowMs: number,
  currentRunGameElapsedMs: number,
): OHovRunRecords {
  const startedAt = normalizeStartedAtMs(current.currentRunStartedAtMs);
  const safeNow = normalizeStartedAtMs(nowMs);
  const runDurationMs = Math.max(0, safeNow - startedAt);
  const previousFastest = current.fastestRunDurationMs;
  const fastestRunDurationMs =
    previousFastest === null
      ? runDurationMs
      : Math.min(previousFastest, runDurationMs);
  const runGameDurationMs = normalizeDurationMs(currentRunGameElapsedMs);
  const previousFastestGame = current.fastestRunGameDurationMs;
  const fastestRunGameDurationMs =
    previousFastestGame === null
      ? runGameDurationMs
      : Math.min(previousFastestGame, runGameDurationMs);
  return {
    currentRunStartedAtMs: safeNow,
    lastRunDurationMs: runDurationMs,
    fastestRunDurationMs,
    currentRunGameElapsedMs: 0,
    fastestRunGameDurationMs,
  };
}

/**
 * Coerce externally supplied OHov records (legacy save, missing fields, NaN)
 * into a valid shape. Invalid duration values fall back to null; invalid start
 * times fall back to the supplied `fallbackNowMs`.
 */
export function normalizeOHovRunRecords(
  raw: Partial<OHovRunRecords> | undefined,
  fallbackNowMs: number,
): OHovRunRecords {
  return {
    currentRunStartedAtMs: normalizeStartedAtMs(
      raw?.currentRunStartedAtMs,
      fallbackNowMs,
    ),
    lastRunDurationMs: normalizeOptionalDurationMs(raw?.lastRunDurationMs),
    fastestRunDurationMs: normalizeOptionalDurationMs(
      raw?.fastestRunDurationMs,
    ),
    currentRunGameElapsedMs: normalizeDurationMs(raw?.currentRunGameElapsedMs),
    fastestRunGameDurationMs: normalizeOptionalDurationMs(
      raw?.fastestRunGameDurationMs,
    ),
  };
}

function normalizeStartedAtMs(value: unknown, fallback: number = 0): number {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return value;
  }
  if (
    typeof fallback === "number" &&
    Number.isFinite(fallback) &&
    fallback >= 0
  ) {
    return fallback;
  }
  return 0;
}

function normalizeOptionalDurationMs(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return null;
  }
  return value;
}

function normalizeDurationMs(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return value;
  }
  return 0;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
}
